import { Container } from 'src/components-v2/Layout';
import PlaidLogo from 'src/components-v2/SVGs/PlaidLogo';
import PlaidLogoWaffle from 'src/components-v2/SVGs/PlaidLogoWaffle';
import styled from 'threads5/styles/styled';

const SUBNAV_DESKTOP_HEIGHT = 86;
const SUBNAV_MOBILE_HEIGHT = 72;
const FIXED_STATE_TRANSITION = '.2s ease';

export const StyledSubnavPlaceholder = styled('div')(({ theme }) => {
  return {
    height: `${SUBNAV_MOBILE_HEIGHT}px`,
    [theme.breakpoints.up('md')]: {
      height: `${SUBNAV_DESKTOP_HEIGHT}px`,
    },
  };
});

export const StyledSubnav = styled('div')<{
  isFixed: boolean;
}>(({ isFixed, theme }) => {
  return {
    background: 'rgba(255, 255, 255, 1)',
    backdropFilter: 'blur(12px)',
    height: `${SUBNAV_MOBILE_HEIGHT}px`,
    position: 'relative',
    zIndex: 10,
    boxShadow: '0 4px 10px 0 rgba(0, 0, 0, .10)',
    transition: FIXED_STATE_TRANSITION,
    transitionProperty: 'background-color',
    ...(isFixed && {
      background: 'rgba(255, 255, 255, .8)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100_000_000, // insane z-index that is our main website navigation + 1
    }),
    [theme.breakpoints.up('md')]: {
      height: `${SUBNAV_DESKTOP_HEIGHT}px`,
    },
  };
});

export const StyledOverflowIndicators = styled('div')<{
  hasLeftOverflow: boolean;
  hasRightOverflow: boolean;
}>(({ hasLeftOverflow, hasRightOverflow }) => {
  return {
    position: 'relative',
    height: '100%',
    overflow: 'hidden', // Prevent indicators from causing horizontal scrollbars when hidden
    '::before,::after': {
      content: '""',
      height: '100%',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '32px',
      pointerEvents: 'none',
      background: 'red',
      zIndex: 1,
      opacity: 0,
      transition: '.2s ease',
      transitionProperty: 'opacity, translate',
    },
    '::before': {
      left: 0,
      background: 'linear-gradient(to left, transparent, rgba(0, 0, 0, .2))',
      translate: '-16px',
      ...(hasLeftOverflow && {
        opacity: 1,
        translate: 'none',
      }),
    },
    '::after': {
      right: 0,
      background: 'linear-gradient(to right, transparent, rgba(0, 0, 0, .2))',
      translate: '16px',
      ...(hasRightOverflow && {
        opacity: 1,
        translate: 'none',
      }),
    },
  };
});

export const StyledOverflow = styled('div')({
  overflow: 'auto',
  width: '100%',
  height: '100%',
  position: 'relative',
});

export const StyledFlexCol = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minWidth: 'min-content', // Ensures flexbox does not clip padding when overflowing
});

export const StyledContainer = styled(Container)({
  display: 'flex',
  flexGrow: 1,
  alignItems: 'stretch',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
});

export const StyledNav = styled('nav')(({ theme }) => {
  return {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content) minmax(max-content, auto)', // Allow the CTA to fill up remaining space
    alignItems: 'center',
    padding: '0 18px 0 24px', // Matches `MainNavigationBar.tsx` padding
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns:
        'minmax(max-content, 1fr) auto minmax(max-content, 1fr)',
      margin: '0 calc(100% * 2 / 24)', // Mimics `<Column lgOffset={2} lg={20}>` while allowing layout to overflow
    },
  };
});

export const StyledLogoAnchor = styled('a')({
  transition: ' .2s ease',
  transitionProperty: 'opacity, scale',
  display: 'inline-block',
  ':hover': {
    opacity: 0.8,
  },
  ':active': {
    scale: 0.96,
  },
});

export const StyledLogoWrapper = styled('div')<{
  isParentFixed: boolean;
}>(({ isParentFixed, theme }) => {
  return {
    transition: FIXED_STATE_TRANSITION,
    transitionProperty:
      'opacity, visibility, translate, scale, padding-right, width',
    paddingRight: '2.4rem',
    [theme.breakpoints.up('lg')]: {
      paddingRight: '4rem',
    },
    ...(!isParentFixed && {
      opacity: 0,
      visibility: 'hidden',
      translate: '0 8px',
      [theme.breakpoints.down('lg')]: {
        scale: 0.8,
        translate: 'none',
        width: 0,
        paddingRight: 0,
      },
    }),
  };
});

export const StyledPlaidLogo = styled(PlaidLogo)(({ theme }) => {
  return {
    height: '28px',
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  };
});

export const StyledPlaidLogoWaffle = styled(PlaidLogoWaffle)(({ theme }) => {
  return {
    height: '24px',
    [theme.breakpoints.up('sm')]: {
      height: '28px',
    },
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  };
});

export const StyledCtaWrapper = styled('div')<{
  isParentFixed: boolean;
}>(({ isParentFixed, theme }) => {
  return {
    transition: FIXED_STATE_TRANSITION,
    transitionProperty: 'opacity, visibility, translate',
    justifySelf: 'flex-end',
    ...(!isParentFixed && {
      [theme.breakpoints.up('lg')]: {
        opacity: 0,
        visibility: 'hidden',
        translate: '0 8px',
      },
    }),
  };
});

export const StyledCta = styled('a')(({ theme }) => {
  return {
    borderRadius: '24px',
    color: theme.palette['base/black/1000'],
    display: 'inline-block;',
    fontFamily: theme.typography.cern,
    fontSize: '1.6rem',
    fontWeight: 600,
    letterSpacing: '-0.024rem',
    lineHeight: '2.4rem',
    padding: '7.5px 17.5px',
    position: 'relative',
    transition: '.1s ease',
    transitionProperty: 'background, box-shadow, scale',
    marginLeft: '2.4rem',
    [theme.breakpoints.up('lg')]: {
      marginLeft: '4rem',
    },
    '&::before': {
      background:
        theme.palette['web3/component/button/secondary/resting-border'],
      border: '2px solid transparent',
      borderRadius: 'inherit',
      content: '""',
      inset: 0,
      mask: `linear-gradient(#fff) content-box exclude, linear-gradient(#fff)`,
      overflow: 'hidden',
      position: 'absolute',
      zIndex: 1,
    },
    ':hover': {
      background:
        theme.palette['web3/component/button/secondary/interacted-fill'],
      boxShadow: theme.palette['web3/component/button/secondary/box-shadow'],
      '::before': {
        background:
          theme.palette['web3/component/button/secondary/interacted-border'],
      },
    },
    ':active': {
      boxShadow: 'none',
      scale: 0.98,
    },
    ':focus-visible': {
      outline: '2px solid',
      outlineColor: theme.palette['web3/component/button/outline'],
      outlineOffset: '3px',
    },
  };
});

export const StyledLinks = styled('ul')(({ theme }) => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    listStyle: 'none',
    margin: 0,
    gap: '2.5rem',
    minWidth: 0,
    [theme.breakpoints.up('lg')]: {
      gap: '4rem',
    },
    [theme.breakpoints.up('xxl')]: {
      gap: '7rem',
    },
  };
});

export const StyledLink = styled('li')<{ isActive: boolean }>(
  ({ theme, isActive }) => {
    return {
      minWidth: 0,
      a: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette['base/sky/1000'],
        transition: '.2s ease',
        transitionProperty: 'opacity, scale',
        [theme.breakpoints.up('xl')]: {
          gap: '.75rem',
        },
        [theme.breakpoints.up('xxl')]: {
          gap: '1.25rem',
        },
        ...(isActive && {
          scale: 1.015,
          margin: '0 -1px', // Counteract fontWeight layout shift
        }),
        ':hover, :focus-visible': {
          opacity: 0.75,
        },
        ':active': {
          scale: 0.98,
        },
      },
    };
  },
);

export const StyledLinkNumber = styled('span')<{ isActive: boolean }>(
  ({ theme, isActive }) => {
    return {
      fontFamily: theme.typography.boweryStreet,
      fontSize: '1.8rem',
      fontWeight: 500,
      marginTop: '.45rem',
      lineHeight: '2.16rem',
      [theme.breakpoints.down('xl')]: {
        display: 'none',
      },
      ...(isActive && {
        fontWeight: 700,
      }),
    };
  },
);

export const StyledLinkSeparator = styled('span')(({ theme }) => {
  return {
    height: '1px',
    width: '16px',
    flexShrink: 0,
    background: theme.palette['base/sky/1000'],
    [theme.breakpoints.down('xl')]: {
      display: 'none',
    },
  };
});

export const StyledLinkText = styled('span')<{ isActive: boolean }>(
  ({ theme, isActive }) => {
    return {
      fontFamily: theme.typography.cern,
      fontSize: '1.8rem',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '2.16rem',
      letterSpacing: '-0.054rem',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      ...(isActive && {
        fontWeight: 600,
      }),
    };
  },
);

export const StyledProgressBar = styled('div')(() => {
  return {
    height: '8px',
    background: 'rgba(209, 213, 236, .3)',
  };
});

export const StyledProgressBarShape = styled('div')({
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  img: {
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    position: 'absolute',
    inset: '0',
  },
});
